import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import axios from 'axios';
import styles from './CashAdjustment.module.css';

import { FIND_BILL_DATA, DELETE_BILL_ADMIN_URL } from '../Api/ApiConstants';

const CashAdjustment = () => {
  const [storeId, setStoreId] = useState('');
  const [amountRange, setAmountRange] = useState('');
  const [adjustmentData, setAdjustmentData] = useState([]);
  const [selectedBills, setSelectedBills] = useState({});
  const [billsForDeletion, setBillsForDeletion] = useState({});
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [deletionProgress, setDeletionProgress] = useState({ current: 0, total: 0, status: '' });
  const [deletionResults, setDeletionResults] = useState({ success: [], failed: [] });
  const [fetchingProgress, setFetchingProgress] = useState({ current: 0, total: 0, isActive: false });
  const [warnings, setWarnings] = useState({});
  const [dismissedWarnings, setDismissedWarnings] = useState({});
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFileName, setSelectedFileName] = useState('');
  const [showDeletionPopup, setShowDeletionPopup] = useState(false);
  const [deletionList, setDeletionList] = useState([]);

  // Popup states
  const [showBillPopup, setShowBillPopup] = useState(false);
  const [selectedDateIndex, setSelectedDateIndex] = useState(null);
  const [showReviewPopup, setShowReviewPopup] = useState(false);

  const stores = ['NX', 'VN', 'VN_NEW'];

  useEffect(() => {
    const user = JSON.parse(sessionStorage.getItem('user'));
    if (user?.storeId) {
      setStoreId(user.storeId);
    }
  }, []);

  const handleReset = () => {
    setStoreId('');
    setAmountRange('');
    setAdjustmentData([]);
    setSelectedBills({});
    setBillsForDeletion({});
    setCurrentStep(1);
    setDeletionProgress({ current: 0, total: 0, status: '' });
    setDeletionResults({ success: [], failed: [] });
    setFetchingProgress({ current: 0, total: 0, isActive: false });
    setWarnings({});
    setDismissedWarnings({});
    setSelectedFileName('');
    setShowUploadModal(false);
    setShowDeletionPopup(false);
    setDeletionList([]);
  };

  const dismissWarning = (index) => {
    setDismissedWarnings(prev => ({ ...prev, [index]: true }));
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!storeId || !amountRange) {
      alert('Please select Store ID and Amount Range first');
      e.target.value = '';
      return;
    }

    setSelectedFileName(file.name);
    
    const reader = new FileReader();
    reader.onload = (event) => {
      const data = new Uint8Array(event.target.result);
      const workbook = XLSX.read(data, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      const formattedData = jsonData.map(row => ({
        collectionDate: row['Collection Date'],
        collection: parseFloat(row['Collection']) || 0,
        requiredCollection: parseFloat(row['Required Collection']) || 0,
        amountToAdjust: parseFloat(row['Amount To Adjust']) || 0,
        adjustedAmount: 0,
        bills: [],
        loading: false,
        fetchStatus: 'pending'
      }));

      setAdjustmentData(formattedData);
      setShowUploadModal(false);
      fetchBillsSequentially(formattedData);
    };
    reader.readAsArrayBuffer(file);
    e.target.value = '';
  };

  const fetchBillsSequentially = async (dataArray) => {
    if (!storeId || !amountRange) {
      alert('Please select Store ID and Amount Range');
      return;
    }

    const datesToFetch = dataArray
      .map((data, index) => ({ data, index }))
      .filter(({ data }) => data.amountToAdjust > 0);

    if (datesToFetch.length === 0) return;

    setFetchingProgress({ current: 0, total: datesToFetch.length, isActive: true });

    for (let i = 0; i < datesToFetch.length; i++) {
      const { data, index } = datesToFetch[i];
      setFetchingProgress({ current: i + 1, total: datesToFetch.length, isActive: true });
      await fetchBillsForDate(data.collectionDate, index, data.amountToAdjust);
      if (i < datesToFetch.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }

    setFetchingProgress({ current: datesToFetch.length, total: datesToFetch.length, isActive: false });
  };

  const fetchBillsForDate = async (date, index, targetAmount) => {
    setAdjustmentData(prev => {
      const updated = [...prev];
      updated[index].loading = true;
      updated[index].fetchStatus = 'fetching';
      return updated;
    });

    try {
      const response = await axios.get(`${FIND_BILL_DATA}`, {
        params: {
          storeId: storeId,
          date: date,
          paymentMode: 'CASH',
          amountRange: amountRange
        }
      });

      const result = response.data;

      if (result.status === 'success' && result.payload && result.payload.length > 0) {
        const sortedBills = result.payload.sort((a, b) => a.amount - b.amount);
 
        setAdjustmentData(prev => {
          const updated = [...prev];
          updated[index].bills = sortedBills;
          updated[index].loading = false;
          updated[index].fetchStatus = 'completed';
          return updated;
        });

        autoSelectBills(index, sortedBills, targetAmount);
      } else {
        setAdjustmentData(prev => {
          const updated = [...prev];
          updated[index].bills = [];
          updated[index].loading = false;
          updated[index].fetchStatus = 'completed';
          return updated;
        });
      }
    } catch (error) {
      console.error('Error fetching bills:', error);
      setAdjustmentData(prev => {
        const updated = [...prev];
        updated[index].loading = false;
        updated[index].fetchStatus = 'error';
        return updated;
      });
    }
  };

  const autoSelectBills = (dateIndex, bills, targetAmount) => {
    if (!bills || bills.length === 0 || targetAmount <= 0) return;

    const totalAvailable = bills.reduce((sum, bill) => sum + bill.amount, 0);

    if (totalAvailable < targetAmount) {
      setWarnings(prev => ({
        ...prev,
        [dateIndex]: `Warning: Available bills (₹${totalAvailable.toFixed(2)}) are less than target amount (₹${targetAmount.toFixed(2)}). Please select bills manually.`
      }));
      return;
    }

    setWarnings(prev => {
      const updated = { ...prev };
      delete updated[dateIndex];
      return updated;
    });

    const result = selectMinimumBills(bills, targetAmount);
    const selectedMap = {};
    result.selected.forEach(bill => {
      selectedMap[bill.billNo] = true;
    });

    setSelectedBills(prev => ({
      ...prev,
      [dateIndex]: selectedMap
    }));

    updateAdjustedAmount(dateIndex, selectedMap, bills);
  };

  const selectMinimumBills = (bills, targetAmount) => {
    const selected = [];
    let total = 0;
    const sortedBills = [...bills].sort((a, b) => b.amount - a.amount);
    
    for (const bill of sortedBills) {
      if (total >= targetAmount) break;
      if (total + bill.amount <= targetAmount * 1.1) {
        selected.push(bill);
        total += bill.amount;
      }
    }
    
    if (total < targetAmount * 0.9) {
      const remainingBills = bills.filter(bill => !selected.find(s => s.billNo === bill.billNo));
      const sortedRemaining = remainingBills.sort((a, b) => a.amount - b.amount);
      
      for (const bill of sortedRemaining) {
        if (total >= targetAmount) break;
        if (total + bill.amount <= targetAmount * 1.1) {
          selected.push(bill);
          total += bill.amount;
        }
      }
    }
    
    return { selected, total };
  };

  const updateAdjustedAmount = (dateIndex, selected, bills) => {
    const total = bills
      .filter(bill => selected[bill.billNo])
      .reduce((sum, bill) => sum + bill.amount, 0);

    setAdjustmentData(prev => {
      const updated = [...prev];
      updated[dateIndex].adjustedAmount = total;
      return updated;
    });
  };

  const toggleBillSelection = (dateIndex, billNo) => {
    setSelectedBills(prev => {
      const dateSelected = prev[dateIndex] || {};
      const updated = {
        ...prev,
        [dateIndex]: {
          ...dateSelected,
          [billNo]: !dateSelected[billNo]
        }
      };

      updateAdjustedAmount(dateIndex, updated[dateIndex], adjustmentData[dateIndex].bills);
      return updated;
    });
  };

  const getSelectedBillCount = (dateIndex) => {
    if (!selectedBills[dateIndex]) return 0;
    return Object.values(selectedBills[dateIndex]).filter(Boolean).length;
  };

  const getSelectedBillAmount = (dateIndex) => {
    if (!selectedBills[dateIndex] || !adjustmentData[dateIndex]) return 0;
    return adjustmentData[dateIndex].bills
      .filter(bill => selectedBills[dateIndex][bill.billNo])
      .reduce((sum, bill) => sum + bill.amount, 0);
  };

  const openBillPopup = (index) => {
    setSelectedDateIndex(index);
    setShowBillPopup(true);
  };

  const closeBillPopup = () => {
    setShowBillPopup(false);
    setSelectedDateIndex(null);
  };

  const prepareDeletion = () => {
    const billsToDelete = {};
    adjustmentData.forEach((data, index) => {
      if (selectedBills[index]) {
        billsToDelete[index] = data.bills.filter(
          bill => selectedBills[index][bill.billNo]
        );
      }
    });
    setBillsForDeletion(billsToDelete);
    setShowReviewPopup(true);
  };

  const handleDeleteBill = async (billNo, storeId) => {
    try {
      const response = await axios.post(
        `${DELETE_BILL_ADMIN_URL}?billNo=${billNo}&storeId=${storeId}`
      );
      if(response.data.status==='success')
      {
        return { success: true, billNo, response: response.data };
      }else{
        return { success: false, billNo, error:response.data.message };
      }
    } catch (error) {
      console.error('Error deleting bill:', error);
      return { success: false, billNo, error: error.message };
    }
  };

  const confirmDeletion = async () => {
    setShowReviewPopup(false);
    
    const flatDeletionList = [];
    Object.entries(billsForDeletion).forEach(([dateIndex, bills]) => {
      bills.forEach(bill => {
        flatDeletionList.push({
          billNo: bill.billNo,
          amount: bill.amount,
          storeId: bill.storeId,
          date: adjustmentData[dateIndex].collectionDate,
          status: 'pending'
        });
      });
    });

    setDeletionList(flatDeletionList);
    setShowDeletionPopup(true);

    const results = { success: [], failed: [] };

    for (let i = 0; i < flatDeletionList.length; i++) {
      const bill = flatDeletionList[i];
      
      setDeletionList(prev => {
        const updated = [...prev];
        updated[i] = { ...updated[i], status: 'processing' };
        return updated;
      });

      const result = await handleDeleteBill(bill.billNo, bill.storeId);
      
      setDeletionList(prev => {
        const updated = [...prev];
        updated[i] = { 
          ...updated[i], 
          status: result.success ? 'success' : 'failed',
          error: result.error 
        };
        return updated;
      });

      if (result.success) {
        results.success.push({ billNo: bill.billNo, amount: bill.amount, date: bill.date });
      } else {
        results.failed.push({ billNo: bill.billNo, amount: bill.amount, date: bill.date, error: result.error });
      }

      await new Promise(resolve => setTimeout(resolve, 300));
    }

    setDeletionResults(results);

    if (results.failed.length === 0) {
      setTimeout(() => {
        alert(`Successfully deleted all ${results.success.length} bills!`);
      }, 500);
    } else {
      setTimeout(() => {
        alert(
          `Deletion completed:\n✓ Success: ${results.success.length} bills\n✗ Failed: ${results.failed.length} bills`
        );
      }, 500);
    }
  };

  const getAdjustmentStatus = (adjusted, required) => {
    if (adjusted === 0) return styles.statusNone;
    const percentage = (adjusted / required) * 100;
    if (percentage >= 90 && percentage <= 110) return styles.statusGood;
    if (percentage >= 80 && percentage <= 120) return styles.statusOk;
    return styles.statusPoor;
  };

  const retryFailedFetches = async () => {
    const failedIndices = adjustmentData
      .map((data, index) => ({ data, index }))
      .filter(({ data }) => data.fetchStatus === 'error' && data.amountToAdjust > 0);

    if (failedIndices.length === 0) {
      alert('No failed fetches to retry');
      return;
    }

    for (const { data, index } of failedIndices) {
      await fetchBillsForDate(data.collectionDate, index, data.amountToAdjust);
      await new Promise(resolve => setTimeout(resolve, 500));
    }
  };

  const getSortedBillsForPopup = (dateIndex) => {
    if (!adjustmentData[dateIndex] || !adjustmentData[dateIndex].bills) return [];
    
    const bills = adjustmentData[dateIndex].bills;
    const selected = selectedBills[dateIndex] || {};
    
    const selectedBillsList = bills.filter(bill => selected[bill.billNo]);
    const unselectedBillsList = bills.filter(bill => !selected[bill.billNo]);
    
    return [...selectedBillsList, ...unselectedBillsList];
  };

  const isDeletionComplete = () => {
    return deletionList.every(bill => bill.status === 'success' || bill.status === 'failed');
  };

  const visibleWarnings = Object.entries(warnings).filter(
    ([index]) => !dismissedWarnings[index]
  );
  
  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>Cash Adjustment System</h1>
      </div>
   
      {/* Toast Notifications */}
      {fetchingProgress.isActive && (
        <div className={styles.toast}>
          <div className={styles.toastContent}>
            <div className={styles.toastHeader}>
              <span>⏳ Fetching Bills...</span>
            </div>
            <div className={styles.toastProgressBar}>
              <div 
                className={styles.toastProgressFill}
                style={{ width: `${(fetchingProgress.current / fetchingProgress.total) * 100}%` }}
              />
            </div>
            <div className={styles.toastText}>
              {fetchingProgress.current} of {fetchingProgress.total} dates processed
            </div>
          </div>
        </div>
      )}

      {currentStep === 1 && (
        <div className={styles.uploadSection}>
          {/* Filter Section */}
          <div className={styles.filterSection}>
            <div className={styles.filterGrid}>
              <div className={styles.filterItem}>
                <label className={styles.filterLabel}>
                  Store ID <span className={styles.required}>*</span>
                </label>
                <select 
                  value={storeId} 
                  onChange={(e) => setStoreId(e.target.value)}
                  className={styles.filterSelect}
                  disabled={fetchingProgress.isActive || adjustmentData.length > 0}
                >
                  <option value="">Select Store</option>
                  {stores.map(store => (
                    <option key={store} value={store}>{store}</option>
                  ))}
                </select>
              </div>

              <div className={styles.filterItem}>
                <label className={styles.filterLabel}>
                  Amount Range <span className={styles.required}>*</span>
                </label>
                <input
                  type="number"
                  value={amountRange}
                  onChange={(e) => setAmountRange(e.target.value)}
                  placeholder="Enter amount range"
                  className={styles.filterInput}
                  disabled={fetchingProgress.isActive || adjustmentData.length > 0}
                />
              </div>

              <div className={styles.filterItem}>
                <label className={styles.filterLabel}>
                  Excel File <span className={styles.required}>*</span>
                </label>
                <div className={styles.fileUploadWrapper}>
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileUpload}
                    className={styles.fileInputHidden}
                    id="fileUpload"
                    disabled={!storeId || !amountRange || fetchingProgress.isActive}
                  />
                  <label 
                    htmlFor="fileUpload" 
                    className={`${styles.fileUploadLabel} ${(!storeId || !amountRange) ? styles.fileUploadDisabled : ''}`}
                  >
                    <span className={styles.fileIcon}>📄</span>
                    <span className={styles.fileText}>
                      {selectedFileName || 'Choose File'}
                    </span>
                  </label>
                </div>
              </div>

              <div className={styles.filterItem}>
                <label className={styles.filterLabel}>&nbsp;</label>
                <button 
                  onClick={handleReset}
                  className={styles.btnReset}
                  disabled={fetchingProgress.isActive}
                >
                  🔄 Reset
                </button>
              </div>
            </div>
          </div>

          {/* Data Table */}
          {adjustmentData.length > 0 && (
            <>
              <div className={styles.dataTableWrapper}>
                <table className={styles.mainTable}>
                  <thead>
                    <tr>
                      <th>Collection Date</th>
                      <th>Collection</th>
                      <th>Required Collection</th>
                      <th>Amount To Adjust</th>
                      <th>Bill No Selected</th>
                      <th>Amount to be Deleted</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {adjustmentData.map((data, index) => (
                      <tr key={index}>
                        <td>{data.collectionDate}</td>
                        <td>₹{data.collection.toFixed(2)}</td>
                        <td>₹{data.requiredCollection.toFixed(2)}</td>
                        <td className={data.amountToAdjust > 0 ? styles.highlightAmount : ''}>
                          ₹{data.amountToAdjust.toFixed(2)}
                        </td>
                        <td className={styles.boldText}>{getSelectedBillCount(index)}</td>
                        <td className={styles.boldText}>
                          ₹{getSelectedBillAmount(index).toFixed(2)}
                        </td>
                        <td>
                          {data.amountToAdjust === 0 ? (
                            <span className={styles.statusNA}>N/A</span>
                          ) : data.loading ? (
                            <span className={styles.statusFetching}>Fetching...</span>
                          ) : data.fetchStatus === 'completed' && data.bills.length > 0 ? (
                            <span className={styles.statusSuccess}>✓ Loaded</span>
                          ) : data.fetchStatus === 'completed' && data.bills.length === 0 ? (
                            <span className={styles.statusWarning}>No bills</span>
                          ) : data.fetchStatus === 'error' ? (
                            <span className={styles.statusError}>✗ Error</span>
                          ) : (
                            <span className={styles.statusPending}>Pending</span>
                          )}
                        </td>
                        <td>
                          {data.amountToAdjust > 0 && data.bills.length > 0 && (
                            <button 
                              onClick={() => openBillPopup(index)}
                              className={styles.actionBtn}
                              disabled={data.loading}
                            >
                              👁️ View
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td><strong>Total</strong></td>
                      <td><strong>₹{adjustmentData.reduce((sum, d) => sum + d.collection, 0).toFixed(2)}</strong></td>
                      <td><strong>₹{adjustmentData.reduce((sum, d) => sum + d.requiredCollection, 0).toFixed(2)}</strong></td>
                      <td><strong>₹{adjustmentData.reduce((sum, d) => sum + d.amountToAdjust, 0).toFixed(2)}</strong></td>
                      <td><strong>{adjustmentData.reduce((sum, data, index) => sum + getSelectedBillCount(index), 0)}</strong></td>
                      <td><strong>₹{adjustmentData.reduce((sum, data, index) => sum + getSelectedBillAmount(index), 0).toFixed(2)}</strong></td>
                      <td></td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {visibleWarnings.length > 0 && (
                <div className={styles.warningsSection}>
                  {visibleWarnings.map(([index, message]) => (
                    <div key={index} className={styles.warningMessage}>
                      <span className={styles.warningText}>⚠ {adjustmentData[index].collectionDate}: {message}</span>
                      <button
                        className={styles.warningCloseBtn}
                        onClick={() => dismissWarning(index)}
                        title="Dismiss warning"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className={styles.actionButtons}>
                {adjustmentData.some(d => d.fetchStatus === 'error') && (
                  <button 
                    onClick={retryFailedFetches} 
                    className={styles.btnWarning}
                    disabled={fetchingProgress.isActive}
                  >
                    🔄 Retry Failed Fetches
                  </button>
                )}
                <button 
                  onClick={prepareDeletion} 
                  disabled={loading || fetchingProgress.isActive}
                  className={styles.btnPrimary}
                >
                  📋 Review Selected Bills
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Bill Selection Popup */}
      {showBillPopup && selectedDateIndex !== null && (
        <div className={styles.popupOverlay} onClick={closeBillPopup}>
          <div className={styles.popupContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.popupHeader}>
              <h3>Bills for {adjustmentData[selectedDateIndex].collectionDate}</h3>
              <button onClick={closeBillPopup} className={styles.closeBtn}>×</button>
            </div>
            <div className={styles.popupInfo}>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Target Amount:</span>
                <span className={styles.infoValue}>₹{adjustmentData[selectedDateIndex].amountToAdjust.toFixed(2)}</span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Selected Amount:</span>
                <span className={styles.infoValue}>₹{getSelectedBillAmount(selectedDateIndex).toFixed(2)}</span>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Selected Bills:</span>
                <span className={styles.infoValue}>{getSelectedBillCount(selectedDateIndex)}</span>
              </div>
            </div>
            {warnings[selectedDateIndex] && (
              <div className={styles.popupWarning}>
                ⚠ {warnings[selectedDateIndex]}
              </div>
            )}
            <div className={styles.popupTableWrapper}>
              <table className={styles.popupTable}>
                <thead>
                  <tr>
                    <th>Select</th>
                    <th>Bill No</th>
                    <th>Amount</th>
                    <th>Priority</th>
                  </tr>
                </thead>
                <tbody>
                  {getSortedBillsForPopup(selectedDateIndex).map((bill, billIndex) => {
                    const allBills = adjustmentData[selectedDateIndex].bills;
                    const originalIndex = allBills.findIndex(b => b.billNo === bill.billNo);
                    const isHighPriority = originalIndex < Math.floor(allBills.length * 0.4);
                    const isMidPriority = originalIndex >= Math.floor(allBills.length * 0.4) && 
                                          originalIndex < Math.floor(allBills.length * 0.8);
                    const isSelected = selectedBills[selectedDateIndex]?.[bill.billNo] || false;
                    
                    return (
                      <tr 
                        key={bill.billNo} 
                        className={isSelected ? styles.selectedRow : ''}
                      >
                        <td>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleBillSelection(selectedDateIndex, bill.billNo)}
                            className={styles.checkbox}
                          />
                        </td>
                        <td>{bill.billNo}</td>
                        <td>₹{bill.amount.toFixed(2)}</td>
                        <td>
                          <span className={
                            isHighPriority ? styles.highPriority : 
                            isMidPriority ? styles.midPriority : 
                            styles.lowPriority
                          }>
                            {isHighPriority ? 'High' : isMidPriority ? 'Mid' : 'Low'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className={styles.popupFooter}>
              <button onClick={closeBillPopup} className={styles.btnPrimary}>
                ✓ Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Review Popup */}
      {showReviewPopup && (
        <div className={styles.popupOverlay} onClick={() => setShowReviewPopup(false)}>
          <div className={styles.popupContentLarge} onClick={(e) => e.stopPropagation()}>
            <div className={styles.popupHeader}>
              <h3>📋 Review Bills for Deletion</h3>
              <button onClick={() => setShowReviewPopup(false)} className={styles.closeBtn}>×</button>
            </div>

            <div className={styles.detailedBillsSection}>
              <h4>Detailed Bill List</h4>
              <div className={styles.billGroupsContainer}>
                {Object.entries(billsForDeletion).map(([dateIndex, bills]) => (
                  <div key={dateIndex} className={styles.billGroup}>
                    <h5>{adjustmentData[dateIndex].collectionDate}</h5>
                    <div className={styles.billList}>
                      {bills.map(bill => (
                        <div key={bill.billNo} className={styles.billItem}>
                          <span className={styles.billNo}>Bill #{bill.billNo}</span>
                          <span className={styles.billAmount}>₹{bill.amount.toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className={styles.popupFooter}>
              <button onClick={() => setShowReviewPopup(false)} className={styles.btnSecondary}>
                Cancel
              </button>
              <button onClick={confirmDeletion} className={styles.btnDanger}>
                🗑️ Confirm Deletion ({Object.values(billsForDeletion).reduce((sum, bills) => sum + bills.length, 0)} bills)
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bill Deletion Status Popup */}
      {showDeletionPopup && (
        <div className={styles.deletionPopupOverlay}>
          <div className={styles.deletionPopupContent}>
            <div className={styles.deletionPopupHeader}>
              <h2>Bill Deletion Status</h2>
              <p className={styles.deletionSubtext}>Bills are being processed one-by-one. Please wait.</p>
            </div>
            
            <div className={styles.deletionTableWrapper}>
              <table className={styles.deletionTable}>
                <thead>
                  <tr>
                    <th>Bill No</th>
                    <th>Amount</th>
                    <th>Store ID</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {deletionList.map((bill, index) => (
                    <tr key={index} className={index % 2 === 0 ? styles.zebraRow : ''}>
                      <td>{bill.billNo}</td>
                      <td>₹{bill.amount.toFixed(2)}</td>
                      <td>{bill.storeId}</td>
                      <td>
                        {bill.status === 'pending' && (
                          <span className={styles.statusBadgePending}>Pending</span>
                        )}
                        {bill.status === 'processing' && (
                          <span className={styles.statusBadgeProcessing}>
                            <span className={styles.spinner}></span> Processing
                          </span>
                        )}
                        {bill.status === 'success' && (
                          <span className={styles.statusBadgeSuccess}>
                            <span className={styles.successIcon}>✓</span> Success
                          </span>
                        )}
                        {bill.status === 'failed' && (
                          <span className={styles.statusBadgeFailed}>
                            <span className={styles.failedIcon}>✗</span> Failed
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className={styles.deletionPopupFooter}>
              <button 
                onClick={() => {
                  setShowDeletionPopup(false);
                  handleReset();
                }}
                className={styles.btnCloseDeletion}
                disabled={!isDeletionComplete()}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CashAdjustment;